/**
 * FC.11 — useCotejo hook
 *
 * Orquesta la ejecución del cotejo de completitud desde el wizard.
 * Crea el servicio con los singletons R2 reales y gestiona el ciclo
 * loading → resultado | error.
 *
 * @module hooks/useCotejo
 */

import { useState, useCallback, useRef } from 'react';
import type { ResultadoCotejo } from '../services/cotejo/types';
import type { ProcessedInfrastructure } from '../types/processing';
import { CotejoCompletitudService } from '../services/cotejo/CotejoCompletitudService';
import { InventarioReferenciaBuilder } from '../services/cotejo/InventarioReferenciaBuilder';
import { getDERAR2Service } from '../services/dera/DERAR2Service';
import { getDERAG10R2Service } from '../services/geocoding/specialized/DERAG10R2Service';
import { getDERAG3R2Service } from '../services/geocoding/specialized/DERAG3R2Service';
import { getIAPHR2Service } from '../services/geocoding/specialized/IAPHR2Service';
import { getEduCentersR2Service } from '../services/geocoding/specialized/EduCentersR2Service';
import { getGCFacilitiesR2Service } from '../services/geocoding/specialized/GCFacilitiesR2Service';
import { getOSMSecurityR2Service } from '../services/geocoding/specialized/OSMSecurityR2Service';
import { getAGOLDataService } from '../services/data/AGOLDataService';
import { useAudit } from './useAudit';

/**
 * TERMCOT-1 DOP-5 b: texto de reserva cuando el fallo no trae mensaje propio.
 * P3 lo pinta tras «Error al cotejar: », por eso no empieza por «Error».
 */
export const MENSAJE_ERROR_COTEJO = 'no se pudo completar el cotejo con el catálogo oficial';

// =============================================================================
// FACTORY — cableado R2 singletons → CotejoCompletitudService
// =============================================================================

function createCotejoService(): CotejoCompletitudService {
  const builder = new InventarioReferenciaBuilder({
    deraG12: getDERAR2Service(),
    deraG10: getDERAG10R2Service(),
    deraG3: getDERAG3R2Service(),
    iaph: getIAPHR2Service(),
    eduCenters: getEduCentersR2Service(),
    gcFacilities: getGCFacilitiesR2Service(),
    osmSecurity: getOSMSecurityR2Service(),
    agol: getAGOLDataService(),
  });
  return new CotejoCompletitudService(builder);
}

// =============================================================================
// HOOK
// =============================================================================

export interface UseCotejoReturn {
  resultado: ResultadoCotejo | null;
  loading: boolean;
  error: string | null;
  ejecutar: (infras: ProcessedInfrastructure[], ine5: string, municipio: string) => Promise<void>;
  reset: () => void;
}

export function useCotejo(): UseCotejoReturn {
  const [resultado, setResultado] = useState<ResultadoCotejo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { logDocument } = useAudit();
  /** Evita ejecuciones duplicadas concurrentes */
  const runningRef = useRef(false);

  const ejecutar = useCallback(async (
    infras: ProcessedInfrastructure[],
    ine5: string,
    municipio: string,
  ) => {
    if (runningRef.current) return;
    runningRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const service = createCotejoService();
      const result = await service.ejecutarCotejo(infras, ine5, municipio);
      setResultado(result);
      // Audit: cotejo completado
      void logDocument('create', `cotejo-${ine5}`, ine5, null, {
        municipio,
        incluidos: result.estadisticas.incluidos,
        ausentes: result.estadisticas.ausentesCriticos + result.estadisticas.ausentesRecomendados,
        sobrantes: result.estadisticas.sobrantes,
        cobertura: Math.round(result.estadisticas.ratioCoberturaGlobal * 100),
      });
    } catch (err) {
      console.error('[useCotejo] Error en cotejo:', err);
      const errorMsg = err instanceof Error ? err.message : MENSAJE_ERROR_COTEJO;
      setError(errorMsg);
      // Audit: cotejo fallido
      void logDocument('create', `cotejo-error-${ine5}`, ine5, null, {
        municipio, error: errorMsg,
      });
    } finally {
      setLoading(false);
      runningRef.current = false;
    }
  }, [logDocument]);

  const reset = useCallback(() => {
    setResultado(null);
    setError(null);
    setLoading(false);
    runningRef.current = false;
  }, []);

  return { resultado, loading, error, ejecutar, reset };
}
